import { useRef, useMemo, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import clsx from 'clsx';
import useFetchProfits, { type ProfitsSearchParams } from '../api/useFetchProfits';
import useFetchProfitsMonthlyTotals from '../api/useFetchProfitsMonthlyTotals';
import ProfitsList from '../components/ProfitsList';
import ProfitsSearchBox, { type SearchConditions } from '../components/ProfitsSearchBox';
import ProfitChart from '../components/ProfitChart';
import ProfitStatusModal from '../components/ProfitStatusModal';
import ProfitSidebar from '../components/ProfitSidebar';
import useConfirmEntry from '../api/useConfirmEntry';
import useSendInvoice from '../api/useSendInvoice';
import usePayment from '../api/usePayment';
import useRefund from '../api/useRefund';
import useRefunded from '../api/useRefunded';
import useCandidateDecline from '../api/useCandidateDecline';
import useCompanyDecline from '../api/useCompanyDecline';
import useDeleteProfit from '../api/useDeleteProfit';
import { currentSelectProfitAtom } from '../atoms/profit';
import Loading from '@/components/Loading';
import Select from '@/components/Select';
import { PROFIT_SORT_ITEMS } from '../constants';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import type { AgentApiProfitsItem } from '@/types/serializers';
import type { ProfitAction } from '../components/ProfitStatusMenu';

const sortItems = PROFIT_SORT_ITEMS.map((item) => ({
  value: item.value,
  label: item.label,
}));

// 日付欄の既定値として使う「今日」の YYYY-MM-DD 文字列
const todayString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type ModalState = {
  profit: AgentApiProfitsItem;
  action: ProfitAction;
} | null;

const modalConfigs: Record<
  ProfitAction,
  {
    title: string;
    description: string;
    confirmLabel: string;
    variant?: 'default' | 'danger';
    successMessage: string;
  }
> = {
  sendInvoice: {
    title: '請求書送付',
    description: '請求書を送付しますか？',
    confirmLabel: '送付',
    successMessage: '請求書送付を登録しました',
  },
  confirmEntry: {
    title: '入社確定',
    description: '入社を確定しますか？',
    confirmLabel: '確定',
    successMessage: '入社を確定しました',
  },
  payment: {
    title: '入金確定',
    description: '入金を確定しますか？',
    confirmLabel: '確定',
    successMessage: '入金を確定しました',
  },
  refund: {
    title: '返金処理開始',
    description: '返金処理を開始しますか？',
    confirmLabel: '開始',
    successMessage: '返金処理を開始しました',
  },
  refunded: {
    title: '返金確定',
    description: '返金を確定しますか？',
    confirmLabel: '確定',
    successMessage: '返金を確定しました',
  },
  candidateDecline: {
    title: '求職者辞退',
    description: '求職者辞退を確定しますか？この操作は取り消せません。',
    confirmLabel: '辞退確定',
    variant: 'danger',
    successMessage: '求職者辞退を確定しました',
  },
  companyDecline: {
    title: '企業NG',
    description: '企業NGを確定しますか？この操作は取り消せません。',
    confirmLabel: '確定',
    variant: 'danger',
    successMessage: '企業NGを確定しました',
  },
  delete: {
    title: '売上票削除',
    description: 'この売上票を削除しますか？',
    confirmLabel: '削除',
    variant: 'danger',
    successMessage: '売上票を削除しました',
  },
};

// アクションごとの日付欄定義を組み立てる。
// confirmEntry のみ「入社確定日」「入金予定日」の2欄を持ち、
// 入金予定日の既定値は「今日」だと Profit モデルの
// payment_date >= entry_expect_date バリデーションに抵触しうるため、
// 入社予定日 → 今日の順にフォールバックさせる。
const buildDateFields = (
  action: ProfitAction,
  profit: AgentApiProfitsItem
): { key: string; label: string; defaultValue: string }[] => {
  const today = todayString();
  switch (action) {
    case 'sendInvoice':
      return [{ key: 'date', label: '請求書送付日', defaultValue: today }];
    case 'confirmEntry':
      return [
        {
          key: 'entry_confirmed_date',
          label: '入社確定日',
          defaultValue: profit.entry_expect_date ?? today,
        },
        {
          key: 'payment_date',
          label: '入金予定日',
          defaultValue: profit.payment_date ?? profit.entry_expect_date ?? today,
        },
      ];
    case 'payment':
      return [{ key: 'date', label: '入金確定日', defaultValue: today }];
    case 'refund':
      return [{ key: 'date', label: '返金予定日', defaultValue: today }];
    case 'refunded':
      return [{ key: 'date', label: '返金確定日', defaultValue: today }];
    case 'candidateDecline':
    case 'companyDecline':
    case 'delete':
      return [];
  }
};

export default function ProfitsTemplate() {
  const queryClient = useQueryClient();
  const [modalState, setModalState] = useState<ModalState>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const currentSelectProfit = useAtomValue(currentSelectProfitAtom);
  const setCurrentSelectProfit = useSetAtom(currentSelectProfitAtom);

  useEffect(() => {
    // Radix の DismissableLayer は pointerdown で外側検知 → Select を閉じるため、
    // mousedown では popper が既に DOM から消えており判定が間に合わない。
    // pointerdown で購読し、Radix が閉じる前の状態でポッパーの有無を判定する。
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target || !sidebarRef.current) return;
      if (sidebarRef.current.contains(target)) return;
      if (target.closest('[data-state="open"]')) return;
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      setIsSidebarOpen(false);
      setIsVisible(false);
      setCurrentSelectProfit(null);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [setCurrentSelectProfit]);

  const openSidebar = (isValue: boolean) => {
    setIsSidebarOpen(isValue);
    setIsVisible(isValue);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setTimeout(() => {
      setIsVisible(false);
      setCurrentSelectProfit(null);
    }, 50);
  };

  const [searchConditions, setSearchConditions] = useState<SearchConditions>({
    query: '',
    status: '',
    companyOrganizationUserId: '',
    candidateOrganizationUserId: '',
    companyId: '',
    teamId: '',
    sort: 'updated_at',
    entryDateFrom: '',
    entryDateTo: '',
  });

  const searchParams = useMemo<ProfitsSearchParams>(() => {
    const params: ProfitsSearchParams = {};
    if (searchConditions.query) params.query = searchConditions.query;
    if (searchConditions.status) params.status = searchConditions.status;
    if (searchConditions.companyOrganizationUserId)
      params.company_organization_user_id = searchConditions.companyOrganizationUserId;
    if (searchConditions.candidateOrganizationUserId)
      params.candidate_organization_user_id = searchConditions.candidateOrganizationUserId;
    if (searchConditions.companyId) params.company_id = searchConditions.companyId;
    if (searchConditions.teamId) params.team_id = searchConditions.teamId;
    if (searchConditions.sort) params.sort = searchConditions.sort;
    if (searchConditions.entryDateFrom || searchConditions.entryDateTo) {
      params.entry_date_range = {};
      if (searchConditions.entryDateFrom)
        params.entry_date_range.from = searchConditions.entryDateFrom;
      if (searchConditions.entryDateTo) params.entry_date_range.to = searchConditions.entryDateTo;
    }
    return params;
  }, [searchConditions]);

  // 月別チャート用のパラメータは sort/page を除いたもの
  const chartSearchParams = useMemo<ProfitsSearchParams>(() => {
    const { sort: _sort, page: _page, ...rest } = searchParams;
    return rest;
  }, [searchParams]);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useFetchProfits(searchParams);

  const { data: monthlyTotals, isLoading: isChartLoading } =
    useFetchProfitsMonthlyTotals(chartSearchParams);

  const profits = useMemo<AgentApiProfitsItem[]>(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.records);
  }, [data]);

  const totalCount = data?.pages?.[0]?.total_count ?? 0;

  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    if (scrollTop + clientHeight >= scrollHeight - 500) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // ステータス変更アクション
  const sendInvoice = useSendInvoice();
  const confirmEntry = useConfirmEntry();
  const payment = usePayment();
  const refund = useRefund();
  const refunded = useRefunded();
  const candidateDecline = useCandidateDecline();
  const companyDecline = useCompanyDecline();
  const deleteProfit = useDeleteProfit();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['/agent/api/profits'] });
  };

  const handleAction = (profit: AgentApiProfitsItem, action: ProfitAction) => {
    setModalState({ profit, action });
  };

  const handleConfirm = async (dates: Record<string, string>) => {
    if (!modalState) return;
    const { profit, action } = modalState;
    const config = modalConfigs[action];
    try {
      switch (action) {
        case 'sendInvoice':
          await sendInvoice.mutateAsync({ id: profit.id, date: dates.date });
          break;
        case 'confirmEntry':
          await confirmEntry.mutateAsync({
            id: profit.id,
            entry_confirmed_date: dates.entry_confirmed_date,
            payment_date: dates.payment_date,
          });
          break;
        case 'payment':
          await payment.mutateAsync({ id: profit.id, date: dates.date });
          break;
        case 'refund':
          await refund.mutateAsync({ id: profit.id, date: dates.date });
          break;
        case 'refunded':
          await refunded.mutateAsync({ id: profit.id, date: dates.date });
          break;
        case 'candidateDecline':
          await candidateDecline.mutateAsync(profit.id);
          break;
        case 'companyDecline':
          await companyDecline.mutateAsync(profit.id);
          break;
        case 'delete':
          await deleteProfit.mutateAsync(profit.id);
          break;
      }
      setModalState(null);
      closeSidebar();
      invalidate();
      showSuccessToast(config.successMessage);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'エラーが発生しました');
    }
  };

  const isActionPending =
    sendInvoice.isPending ||
    confirmEntry.isPending ||
    payment.isPending ||
    refund.isPending ||
    refunded.isPending ||
    candidateDecline.isPending ||
    companyDecline.isPending ||
    deleteProfit.isPending;

  return (
    <div className="relative flex flex-col gap-md">
      <div className="flex flex-col gap-md px-lg py-sm">
        <ProfitsSearchBox conditions={searchConditions} onChange={setSearchConditions} />

        {/* 月別売上チャート */}
        <ProfitChart data={monthlyTotals} isLoading={isChartLoading} />

        {/* ソート + 件数 */}
        <div className="flex items-end justify-between gap-4">
          <div className="w-[196px]">
            <Select
              className="w-full"
              value={searchConditions.sort}
              onValueChange={(v) => setSearchConditions((prev) => ({ ...prev, sort: v }))}
              items={sortItems}
            />
          </div>
          {!isLoading && <span className="text-sm text-gray-500">{totalCount}件</span>}
        </div>
      </div>

      <div className="relative">
        <div className="px-md">
          <ProfitsList
            profits={profits}
            isLoading={isLoading}
            onAction={handleAction}
            isSidebarOpen={isSidebarOpen}
            openSidebar={openSidebar}
          />
        </div>
      </div>

      {isFetchingNextPage && (
        <div className="flex justify-center py-md">
          <Loading size="small" />
        </div>
      )}

      <div>
        {/* ステータス変更モーダル */}
        {modalState && (
          <ProfitStatusModal
            open={true}
            onOpenChange={(open) => {
              if (!open) setModalState(null);
            }}
            title={modalConfigs[modalState.action].title}
            description={modalConfigs[modalState.action].description}
            confirmLabel={modalConfigs[modalState.action].confirmLabel}
            dateFields={buildDateFields(modalState.action, modalState.profit)}
            variant={modalConfigs[modalState.action].variant}
            onConfirm={handleConfirm}
            isLoading={isActionPending}
          />
        )}

        {isVisible && (
          <div
            ref={sidebarRef}
            className={clsx(
              'fixed top-[60px] right-0 z-[101] h-[calc(100vh_-_60px)] w-[415px] overflow-hidden bg-white shadow-[0px_0px_2px_0px_rgba(0,_0,_0,_0.12)]',
              isSidebarOpen ? 'animate-slideInRight' : 'animate-fadeOutRight'
            )}
          >
            <div className="flex h-full flex-col overflow-hidden">
              <ProfitSidebar
                closeSidebar={closeSidebar}
                onAction={(action) => {
                  if (currentSelectProfit) {
                    handleAction(currentSelectProfit, action);
                  }
                }}
                isSidebarOpen={isSidebarOpen}
                maxHeight="calc(100vh - 60px)"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
